/**
 * NPS (Net Promoter Score) Transformer
 *
 * Formula:
 *    NPS = ((Promoters / Total) × 100) - ((Detractors / Total) × 100)
 *
 * This transformer extracts promoter, passive, and detractor counts
 * from the chart data for both candidate and hiring manager (HM) surveys,
 * calculates the NPS score, and returns the relevant object depending on tileKey.
 *
 * It also gracefully handles cases where:
 *  - Chart data is empty or missing
 *  - Header indices are not found
 *  - Totals are zero (to avoid division by zero)
 */

export const npsScoreTransformer = ({ response, metadata }) => {
  const { headers = [], table = [] } = response || {};
  const { tileKey } = metadata || {};

  // ---- 1. Handle Empty Chart Case ----
  if (!table.length || !Array.isArray(table[0])) {
    // Return default NPS structure with zeros if chart is empty
    return {
      score: 0,
      promoters: 0,
      passives: 0,
      detractors: 0,
      isEmpty: true,
    };
  }

  // ---- 2. Find Relevant Column Indices ----
  const getIndex = (key) => headers.indexOf(key);

  const candidatePromotersIndex = getIndex('candidate_promoters');
  const candidatePassivesIndex = getIndex('candidate_passives');
  const candidateDetractorsIndex = getIndex('candidate_detractors');
  const hmPromotersIndex = getIndex('hm_promoters');
  const hmPassivesIndex = getIndex('hm_passives');
  const hmDetractorsIndex = getIndex('hm_detractors');

  // ---- 3. Extract Values from the First Row of table ----
  const firstRow = table[0] || [];

  const safeValue = (index) =>
    index !== -1 && firstRow[index] != null ? Number(firstRow[index]) : 0;

  const candidatePromoters = safeValue(candidatePromotersIndex);
  const candidatePassives = safeValue(candidatePassivesIndex);
  const candidateDetractors = safeValue(candidateDetractorsIndex);

  const hmPromoters = safeValue(hmPromotersIndex);
  const hmPassives = safeValue(hmPassivesIndex);
  const hmDetractors = safeValue(hmDetractorsIndex);

  // ---- 4. Compute Totals ----
  const candidateTotal =
    candidatePromoters + candidatePassives + candidateDetractors;
  const hmTotal = hmPromoters + hmPassives + hmDetractors;

  // ---- 5. Compute NPS Safely (Avoid division by zero) ----
  const calculateNps = (promoters, detractors, total) =>
    total > 0 ? (promoters / total) * 100 - (detractors / total) * 100 : 0;

  const candidateNps = calculateNps(
    candidatePromoters,
    candidateDetractors,
    candidateTotal
  );
  const hmNps = calculateNps(hmPromoters, hmDetractors, hmTotal);

  // ---- 6. Calculate Percentages for Display ----
  const candidatePromotersPercent =
    candidateTotal > 0
      ? Math.round((candidatePromoters / candidateTotal) * 100)
      : 0;
  const candidatePassivesPercent =
    candidateTotal > 0
      ? Math.round((candidatePassives / candidateTotal) * 100)
      : 0;
  const candidateDetractorsPercent =
    candidateTotal > 0
      ? Math.round((candidateDetractors / candidateTotal) * 100)
      : 0;

  const hmPromotersPercent =
    hmTotal > 0 ? Math.round((hmPromoters / hmTotal) * 100) : 0;
  const hmPassivesPercent =
    hmTotal > 0 ? Math.round((hmPassives / hmTotal) * 100) : 0;
  const hmDetractorsPercent =
    hmTotal > 0 ? Math.round((hmDetractors / hmTotal) * 100) : 0;

  // ---- 7. Return Result Based on tileKey ----
  const isCandidate = tileKey === 'survey_candidate_nps_card';

  if (isCandidate) {
    return {
      score: Math.round(candidateNps),
      promoters: candidatePromotersPercent,
      passives: candidatePassivesPercent,
      detractors: candidateDetractorsPercent,
    };
  }

  return {
    score: Math.round(hmNps),
    promoters: hmPromotersPercent,
    passives: hmPassivesPercent,
    detractors: hmDetractorsPercent,
  };
};
